/**
 * Runtime assembly of a `BoardAssembly` from the kit (PLAN §6, three tiers):
 *   1. boards generated at their exact size (board.ts) with kit materials per part;
 *   2. segmented details instanced along joint runs at exact lengths (joints.ts) and hardware
 *      placed at `hardware[].at`, never scaled (hardware.ts);
 *   3. LOD: `live` for the stage, `inspect` for close-ups.
 * The result is a THREE.Group in metres, standing on y = 0 and centred on x and z (the live
 * viewer's convention). Board meshes carry `userData.partId`.
 */
import * as THREE from 'three';
import { mergeGeometries } from '../vendor/BufferGeometryUtils.js';
import { boardFrame, createBoardGeometry, grainSeed, orientGeometry } from './board';
import { hardwarePlacements } from './hardware';
import { jointGeometry, lippingRuns, mitreEnds, renderPlacements, type DetailRun, type HousingCap, type MitreEnd } from './joints';
import { materialsFor, resolvePartLook } from './look';
import { SLOT_MATERIAL, type Kit, type KitLike, type PiecePrim } from './loader';
import type { KitAssemblyInput, KitEdgeProfile, KitLod, KitPart, PartLook } from './types';

export interface AssembleOptions {
  lod?: KitLod;
  finish?: string;
  /** Override the part → materials mapping. */
  resolveLook?: (part: KitPart) => PartLook;
  /** Force an edge profile on solid parts ('square' | 'roundover-2'). */
  edgeProfile?: string;
  /** Show hardware marked `visible: false` (dowels) too. */
  includeHiddenHardware?: boolean;
  castShadow?: boolean;
}

export interface AssembledPiece {
  group: THREE.Group;
  partMeshes: Map<string, THREE.Mesh>;
  looks: Map<string, PartLook>;
  /** Display boxes (mm): housed and lipped parts trimmed to what is seen. */
  displayPlacements: Map<string, KitPart['placement']>;
  stats: { parts: number; detailRuns: number; caps: number; hardware: number; drawCalls: number; triangles: number; skipped: string[] };
  /** Placement-frame millimetres → group-local metres. */
  toLocal(mm: { x: number; y: number; z: number }): THREE.Vector3;
  dispose(): void;
}

const ATTRS = ['position', 'normal', 'uv', 'color'] as const;

/** Keep only position / normal / uv / color (white when absent) so pieces merge with boards. */
function normaliseAttributes(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = g.index ? g.toNonIndexed() : g.clone();
  for (const name of Object.keys(out.attributes)) if (!(ATTRS as readonly string[]).includes(name)) out.deleteAttribute(name);
  const n = out.getAttribute('position').count;
  if (!out.getAttribute('uv')) out.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
  if (!out.getAttribute('normal')) out.computeVertexNormals();
  if (!out.getAttribute('color')) out.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(n * 3).fill(1), 3));
  return out;
}

/** The mid prism extruded to `length` metres: far vertices moved, u kept physical. */
export function extrudeMid(g: THREE.BufferGeometry, midLength: number, length: number): THREE.BufferGeometry {
  const out = g.clone();
  const pos = out.getAttribute('position') as THREE.BufferAttribute;
  const nor = out.getAttribute('normal') as THREE.BufferAttribute | undefined;
  const uvs = ['uv', 'uv2'].map((n) => out.getAttribute(n) as THREE.BufferAttribute | undefined);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    if (x > midLength / 2) {
      const dx = length - x;
      pos.setX(i, length);
      const along = !nor || Math.abs(nor.getX(i)) < 0.5;
      for (const uv of uvs) if (uv && along) uv.setX(i, uv.getX(i) + dx);
    }
  }
  pos.needsUpdate = true;
  return out;
}

function frameMatrix(origin: number[], x: number[], y: number[], z: number[], offset: THREE.Vector3): THREE.Matrix4 {
  return new THREE.Matrix4().makeBasis(new THREE.Vector3(...x), new THREE.Vector3(...y), new THREE.Vector3(...z)).setPosition(origin[0] - offset.x, origin[1] - offset.y, origin[2] - offset.z);
}

function capGeometry(cap: HousingCap, look: PartLook, offset: THREE.Vector3): THREE.BufferGeometry {
  const c = new THREE.Vector3(...cap.centre).sub(offset);
  const u = new THREE.Vector3(...cap.u);
  const v = new THREE.Vector3(...cap.v);
  const n = new THREE.Vector3(...cap.normal);
  const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => c.clone().addScaledVector(u, a * cap.halfU).addScaledVector(v, b * cap.halfV));
  // keep the quad facing `n`
  const facing = new THREE.Vector3().crossVectors(u, v).dot(n) > 0;
  const order = facing ? [0, 1, 2, 0, 2, 3] : [0, 2, 1, 0, 3, 2];
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  for (const i of order) {
    const p = corners[i];
    pos.push(p.x, p.y, p.z);
    nor.push(n.x, n.y, n.z);
    const [a, b] = [[-1, -1], [1, -1], [1, 1], [-1, 1]][i];
    // an offset so a mirrored end-grain tile never centres its seam on the cap
    const along = (a * cap.halfU) + 0.047 + Math.abs(cap.centre[0] * 3.1 + cap.centre[1] * 1.7 + cap.centre[2] * 2.3) % 0.1;
    uv.push(along, look.edgeUv === 'normalised' ? (b + 1) / 2 : b * cap.halfV);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(pos.length).fill(1), 3));
  return g;
}

/** Hairlines around a housing cap: the two shoulders and the housing's closed end. */
function capOutline(cap: HousingCap, offset: THREE.Vector3, widthM: number): THREE.BufferGeometry {
  const n = new THREE.Vector3(...cap.normal);
  const c = new THREE.Vector3(...cap.centre).sub(offset).addScaledVector(n, 0.00003);
  const u = new THREE.Vector3(...cap.u);
  const v = new THREE.Vector3(...cap.v);
  const pos: number[] = [];
  const quad = (a: THREE.Vector3, b: THREE.Vector3, across: THREE.Vector3) => {
    const h = across.clone().multiplyScalar(widthM / 2);
    const p = [a.clone().sub(h), b.clone().sub(h), b.clone().add(h), a.clone().add(h)];
    for (const i of [0, 1, 2, 0, 2, 3]) pos.push(p[i].x, p[i].y, p[i].z);
  };
  const at = (su: number, sv: number) => c.clone().addScaledVector(u, su * cap.halfU).addScaledVector(v, sv * cap.halfV);
  quad(at(-1, -1), at(1, -1), v);
  quad(at(-1, 1), at(1, 1), v);
  if (cap.deepU) quad(at(cap.deepU, -1), at(cap.deepU, 1), u);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  const count = pos.length / 3;
  g.setAttribute('normal', new THREE.Float32BufferAttribute(Array.from({ length: count }, () => [n.x, n.y, n.z]).flat(), 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(count * 2), 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(count * 3).fill(1), 3));
  return g;
}

/** The kit's edge profiles, for drawing boards before the kit manifest has loaded. */
export const DEFAULT_EDGE_PROFILES: Record<string, KitEdgeProfile> = {
  square: { radiusMm: 0.6, segments: 1, kind: 'chamfer' },
  'roundover-2': { radiusMm: 2, segments: 4, kind: 'round' },
};

const TINT_SPREAD: Record<string, number> = { sheesham: 0.075, oak: 0.045, 'birch-ply': 0.022 };

/** Kit material ids an assembly needs (to preload before `assembleKit`). */
export function requiredMaterials(input: KitAssemblyInput, opts: AssembleOptions = {}): string[] {
  const resolve = opts.resolveLook ?? ((p: KitPart) => resolvePartLook(p, { edgeProfile: opts.edgeProfile }));
  return materialsFor(input.parts.map(resolve));
}

/** Load the maps an assembly needs, then assemble it. */
export async function assembleKitAsync(kit: Kit, input: KitAssemblyInput, opts: AssembleOptions = {}): Promise<AssembledPiece> {
  await kit.ensureMaterials(requiredMaterials(input, opts));
  return assembleKit(kit, input, opts);
}

/** Edge-profile, segments and strips for one board, and its geometry at `placement` (metres, centred on the box). */
export function boardGeometryFor(edgeProfiles: Record<string, KitEdgeProfile>, part: KitPart, look: PartLook, lod: KitLod, placement: KitPart['placement'] = part.placement, mitres: MitreEnd[] = []): { geometry: THREE.BufferGeometry; centre: [number, number, number]; key: string } {
  const f = boardFrame({ ...part, placement });
  const prof = edgeProfiles[look.edgeProfile] ?? edgeProfiles.square ?? DEFAULT_EDGE_PROFILES.square;
  const segments = lod === 'inspect' ? prof.segments * 2 : prof.segments;
  const strips = part.glueUp ? part.glueUp.strips : 1;
  const key = [f.perm.join(''), ...f.size.map((v) => v.toFixed(6)), look.face, look.edge, look.end, look.edgeUv, look.edgeProfile, look.tint?.join(',') ?? '', strips, segments, mitres.map((m) => `${m.end}${m.outer}${m.depth.toFixed(5)}`).join(',')].join('|');
  const geometry = createBoardGeometry({
    L: f.size[0], W: f.size[1], T: f.size[2],
    radius: prof.radiusMm / 1000, segments, kind: prof.kind,
    strips, edgeUv: look.edgeUv, seed: grainSeed(part.id), tintSpread: TINT_SPREAD[look.species] ?? 0.03, tint: look.tint, mitres,
  });
  orientGeometry(geometry, f.perm);
  return { geometry, centre: f.centre, key };
}

export interface JointLayerOptions {
  lod: KitLod;
  /** Finish per part (or one for all). */
  finish: string | ((partId: string) => string);
  looks: Map<string, PartLook>;
  includeHiddenHardware?: boolean;
  /** Subtracted from every position (metres): the assembly's centring offset. */
  offset?: THREE.Vector3;
  castShadow?: boolean;
}

/** Hardware that is structure, not a fixing (FND-08): shown whether or not "Show fixings" is on.
 *  Feet and legs, and the pulls (knobs, bar handles) on doors and drawers; hinges, runners and the
 *  sliding track stay behind "Show fixings". */
export const STRUCTURAL_HARDWARE = /foot|feet|leg|castor|knob|bar-handle/i;

export interface HardwareInstance {
  partId: string;
  matrix: THREE.Matrix4;
}

export interface JointLayer {
  /** Merged detail meshes (one per material). */
  details: THREE.Mesh[];
  /** One InstancedMesh per hardware piece and slot; `userData.instances` lists each instance's part and base matrix. */
  hardware: THREE.InstancedMesh[];
  detailRuns: number;
  caps: number;
  hardwareCount: number;
  skipped: string[];
  dispose(): void;
}

/** Tier 2: segmented details along every visible joint (and lipping), and hardware at `hardware[].at`. */
export function buildJointLayer(kit: KitLike, input: KitAssemblyInput, o: JointLayerOptions): JointLayer {
  const { lod, looks } = o;
  const offset = o.offset ?? new THREE.Vector3();
  const finishOf = typeof o.finish === 'function' ? o.finish : () => o.finish as string;
  const skipped: string[] = [];
  const geometries: THREE.BufferGeometry[] = [];
  const buckets = new Map<string, { material: THREE.Material; geos: THREE.BufferGeometry[] }>();
  const add = (key: string, material: THREE.Material, g: THREE.BufferGeometry) => {
    const b = buckets.get(key) ?? { material, geos: [] };
    b.geos.push(g);
    buckets.set(key, b);
  };
  const slotMaterial = (slot: string, look: PartLook | undefined, overlay: boolean, finish: string): [string, THREE.Material] | null => {
    if (SLOT_MATERIAL[slot]) {
      const id = SLOT_MATERIAL[slot];
      return [id, kit.material(id, finish)];
    }
    if (!look) return null;
    // lipping on ply is solid oak (its maps are loaded with it); a wooden fixing on ply (a turned
    // foot) takes the part's own face and end, so no extra maps are needed
    const ply = look.species === 'birch-ply';
    const species = ply ? 'oak' : look.species;
    const id = slot === 'slot.face' ? (ply && !overlay ? look.face : `solid-face-${species}`)
      : slot === 'slot.end' ? (ply && !overlay ? look.end : `end-grain-${species}`)
      : slot === 'slot.edge' ? look.edge : null;
    return id ? [`${id}|${finish}${overlay ? '|o' : ''}`, kit.material(id, finish, overlay)] : null;
  };
  const placeRun = (run: DetailRun) => {
    const piece = kit.details.get(run.assetId)?.[lod];
    const meta = kit.manifest.assets[run.assetId];
    if (!piece || !meta) {
      skipped.push(`${run.jointId}: kit lacks ${run.assetId}`);
      return;
    }
    const s = meta.startLength ?? 0;
    const e = meta.endLength ?? 0;
    const m = meta.midLength ?? 1;
    const midLen = run.length - s - e;
    const look = looks.get(run.partId);
    const finish = finishOf(run.partId);
    const lipping = run.assetId === 'detail.lipping-end';
    const base = frameMatrix(run.origin, run.x, run.y, run.z, offset);
    const place = (prims: PiecePrim[], at: number, mid: boolean, length: number) => {
      const mtx = base.clone().multiply(new THREE.Matrix4().makeTranslation(at, 0, 0));
      for (const p of prims) {
        const sm = slotMaterial(p.slot, look, lipping, finish);
        if (!sm) continue;
        const g = mid ? extrudeMid(p.geometry, m, length) : p.geometry.clone();
        g.applyMatrix4(mtx);
        add(sm[0], sm[1], normaliseAttributes(g));
        g.dispose();
      }
    };
    if (midLen < 0) {
      // too short for both ends: drawn as its mid only, at the exact length
      place(piece.mid, 0, true, run.length);
      return;
    }
    place(piece.start, 0, false, 0);
    if (midLen > 1e-5) place(piece.mid, s, true, midLen);
    place(piece.end, run.length, false, 0);
  };

  const jg = jointGeometry(input);
  skipped.push(...jg.skipped.filter((x) => x.reason !== 'not visible').map((x) => `${x.jointId}: ${x.reason}`));
  for (const run of jg.runs) placeRun(run);
  const lipMeta = kit.manifest.assets['detail.lipping-end'];
  const lip = lippingRuns(input, lipMeta?.forThicknessMm ?? 18, lipMeta?.widthMm ?? 6, (p) => looks.get(p.id)?.lipping ?? false);
  skipped.push(...lip.skipped.map((id) => `lipping:${id}: lipping is authored for ${lipMeta?.forThicknessMm ?? 18} mm only`));
  for (const run of lip.runs) placeRun(run);
  for (const cap of jg.caps) {
    const look = looks.get(cap.partId);
    if (!look) continue;
    const finish = finishOf(cap.partId);
    const capMat = cap.shows === 'end' ? look.end : look.edge;
    add(`${capMat}|${finish}|o`, kit.material(capMat, finish, true), capGeometry(cap, look, offset));
    add('slot.shadow', kit.material('slot.shadow', finish), capOutline(cap, offset, lod === 'inspect' ? 0.00035 : 0.0005));
  }
  const details: THREE.Mesh[] = [];
  for (const [key, b] of buckets) {
    const merged = mergeGeometries(b.geos, false);
    for (const g of b.geos) g.dispose();
    if (!merged) continue;
    geometries.push(merged);
    const mesh = new THREE.Mesh(merged, b.material);
    mesh.name = `details:${key}`;
    mesh.userData.kitDetails = true;
    mesh.receiveShadow = true;
    details.push(mesh);
  }

  const hw = hardwarePlacements(input, { includeHidden: o.includeHiddenHardware });
  skipped.push(...hw.skipped);
  // grouped by piece and by the wood of the part it is fixed to (a turned foot is the part's species)
  const byAsset = new Map<string, HardwareInstance[]>();
  const kindOf = new Map<string, string>();
  for (const p of hw.placements) {
    const key = `${p.assetId}|${looks.get(p.partId)?.species ?? ''}`;
    kindOf.set(key, String(p.kind ?? ''));
    const list = byAsset.get(key) ?? [];
    list.push({ partId: p.partId, matrix: frameMatrix(p.origin, p.x, p.y, p.z, offset) });
    byAsset.set(key, list);
  }
  const hardware: THREE.InstancedMesh[] = [];
  for (const [key, list] of byAsset) {
    const assetId = key.split('|')[0];
    const prims = kit.hardware.get(assetId)?.[lod];
    if (!prims) {
      skipped.push(`kit lacks ${assetId}`);
      continue;
    }
    for (const prim of prims) {
      const sm = slotMaterial(prim.slot, looks.get(list[0].partId), false, finishOf(list[0].partId));
      if (!sm) continue;
      const inst = new THREE.InstancedMesh(prim.geometry, sm[1], list.length);
      list.forEach((h, i) => inst.setMatrixAt(i, h.matrix));
      inst.instanceMatrix.needsUpdate = true;
      inst.computeBoundingSphere();
      inst.name = `hardware:${assetId}:${prim.slot}`;
      inst.userData.kitHardware = assetId;
      inst.userData.instances = list;
      // feet, legs and pulls are part of the piece you see: always drawn, unlike screws, dowels and pins
      inst.userData.structural = STRUCTURAL_HARDWARE.test(`${kindOf.get(key) ?? ''} ${assetId}`);
      inst.castShadow = (o.castShadow ?? true) && prim.slot !== 'slot.shadow';
      inst.receiveShadow = true;
      hardware.push(inst);
    }
  }
  return {
    details, hardware,
    detailRuns: jg.runs.length + lip.runs.length, caps: jg.caps.length, hardwareCount: hw.placements.length, skipped,
    dispose() {
      for (const g of geometries) g.dispose();
      for (const h of hardware) h.dispose();
    },
  };
}

/** Display boxes for an assembly (housed and lipped parts trimmed; see renderPlacements). */
export function displayPlacements(kit: KitLike | null, input: KitAssemblyInput, looks: Map<string, PartLook>): Map<string, KitPart['placement']> {
  const lipMeta = kit?.manifest.assets['detail.lipping-end'];
  return renderPlacements(input, { widthMm: lipMeta?.widthMm ?? 6, forThicknessMm: lipMeta?.forThicknessMm ?? 18, wanted: (p) => looks.get(p.id)?.lipping ?? false });
}

/** Toughened glass for glazed doors: transmissive, so the shelves and books show through. */
export const GLASS = new THREE.MeshPhysicalMaterial({ color: 0xe8f2ee, metalness: 0, roughness: 0.04, transmission: 0.92, thickness: 0.004, ior: 1.5, transparent: true, opacity: 0.35, depthWrite: false });
GLASS.name = 'glass';

export function assembleKit(kit: KitLike, input: KitAssemblyInput, opts: AssembleOptions = {}): AssembledPiece {
  const lod: KitLod = opts.lod ?? 'live';
  const finish = opts.finish ?? kit.manifest.defaultFinish;
  const resolve = opts.resolveLook ?? ((p: KitPart) => resolvePartLook(p, { edgeProfile: opts.edgeProfile }));
  const group = new THREE.Group();
  group.name = 'kit-assembly';
  const partMeshes = new Map<string, THREE.Mesh>();
  const looks = new Map<string, PartLook>(input.parts.map((p) => [p.id, resolve(p)]));
  const geometries: THREE.BufferGeometry[] = [];
  const shadows = opts.castShadow ?? true;

  // centre on x and z, floor at y = 0 (placement mm → metres)
  const lo = [Infinity, Infinity, Infinity];
  const hi = [-Infinity, -Infinity, -Infinity];
  for (const p of input.parts) {
    const q = p.placement;
    [q.x, q.y, q.z].forEach((v, i) => (lo[i] = Math.min(lo[i], v)));
    [q.x + q.sx, q.y + q.sy, q.z + q.sz].forEach((v, i) => (hi[i] = Math.max(hi[i], v)));
  }
  const offset = new THREE.Vector3((lo[0] + hi[0]) / 2000, 0, (lo[2] + hi[2]) / 2000);

  // --- tier 1: boards, drawn at their display boxes -----------------------------------
  const shown = displayPlacements(kit, input, looks);
  const mitres = mitreEnds(input, shown);
  for (const part of input.parts) {
    const look = looks.get(part.id)!;
    const { geometry, centre } = boardGeometryFor(kit.manifest.edgeProfiles, part, look, lod, shown.get(part.id), mitres.get(part.id));
    geometries.push(geometry);
    // glass in a glazed door (src/families/fronts.ts): clear and slightly green at the edges, never a timber
    const mesh = new THREE.Mesh(geometry, part.role === 'glass' ? [GLASS, GLASS, GLASS] : [kit.material(look.face, finish), kit.material(look.edge, finish), kit.material(look.end, finish)]);
    mesh.position.set(centre[0] - offset.x, centre[1] - offset.y, centre[2] - offset.z);
    mesh.name = part.id;
    mesh.userData.partId = part.id;
    mesh.castShadow = shadows;
    mesh.receiveShadow = true;
    group.add(mesh);
    partMeshes.set(part.id, mesh);
  }

  // --- tier 2 ---------------------------------------------------------------------------
  const layer = buildJointLayer(kit, input, { lod, finish, looks, includeHiddenHardware: opts.includeHiddenHardware, offset, castShadow: shadows });
  group.add(...layer.details, ...layer.hardware);

  let triangles = 0;
  let drawCalls = 0;
  group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const g = mesh.geometry;
    const count = (g.index ? g.index.count : g.getAttribute('position').count) / 3;
    const inst = (mesh as THREE.InstancedMesh).isInstancedMesh ? (mesh as THREE.InstancedMesh).count : 1;
    triangles += count * inst;
    drawCalls += Array.isArray(mesh.material) ? g.groups.length || 1 : 1;
  });

  return {
    group,
    partMeshes,
    looks,
    displayPlacements: shown,
    stats: { parts: input.parts.length, detailRuns: layer.detailRuns, caps: layer.caps, hardware: layer.hardwareCount, drawCalls, triangles, skipped: layer.skipped },
    toLocal: (p) => new THREE.Vector3(p.x / 1000 - offset.x, p.y / 1000 - offset.y, p.z / 1000 - offset.z),
    dispose() {
      for (const g of geometries) g.dispose();
      layer.dispose();
      group.clear();
    },
  };
}
